import { US_STATES } from "constants/addresses";
import { type UserStateLicense } from "services/types/users.type";

type SignerLicenses = {
  state_licenses?: UserStateLicense[];
  resolved_license_number?: string | null;
  license_number?: string | null;
};

export type SignerLicenseOption = { value: string; label: string };

/**
 * The signer's state licenses, labelled "Maryland — 01505" like the CMS-1500 wizard, plus the
 * license that prints by default when it is not one of them (the legacy license number), so the
 * picker never shows an empty value over a printed number.
 */
export function getSignerLicenseOptions(
  signer: SignerLicenses | null | undefined,
  defaultLicenseNumber?: string
): SignerLicenseOption[] {
  const options = (signer?.state_licenses ?? [])
    .filter((license) => license.state && license.license_number)
    .map((license) => ({
      value: license.license_number,
      label: `${US_STATES.getFromCode(license.state) || license.state} — ${license.license_number}`
    }));
  if (
    defaultLicenseNumber &&
    !options.some((option) => option.value === defaultLicenseNumber)
  ) {
    options.push({
      value: defaultLicenseNumber,
      label: `License on profile — ${defaultLicenseNumber}`
    });
  }
  return options;
}

/**
 * The license the footer prints: a pick made in this dialog, else the one the backend resolved
 * for the form's clinic and visit, else the legacy license number.
 */
export function getPrintedLicenseNumber(
  signer: SignerLicenses | null | undefined,
  pickedLicenseNumber: string | undefined
): string | undefined {
  return (
    pickedLicenseNumber ||
    signer?.resolved_license_number ||
    signer?.license_number ||
    undefined
  );
}
